import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { getErrorMessage } from '@juvare/common-web-ui/utils';
import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import CheckIcon from '@mui/icons-material/Check';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { CircularProgress, FormControl } from '@mui/material';
import React from 'react';
import { AltchaCdn } from '../AltchaCdn';
import { useCommonFormUiTranslation } from '../hooks';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { FormHelperErrorDescription } from '../WidgetForm/Components';
import { callConfigFunction, defaultWidgetLabelProvider, getLabelAndValuePairs, getVisibleError, handleWidgetUpdate, isSkipValidate, joinLabels, makeNewConfigUnique, renderForm, validateCustom, } from '../WidgetForm/Utils';
import { AltchaBox } from './AltchaBox';
import { AltchaWidget } from './AltchaWidget';
import { type } from './types';
const WidgetFormAltcha = (props) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const { internalData, update, render } = props;
    const { t } = useCommonFormUiTranslation();
    const value = props.getDataStringOrUndefined();
    const error = getVisibleError(props);
    const altchaChallengeUrl = (_b = (_a = props.rootParams.globalWidgetConfig) === null || _a === void 0 ? void 0 : _a.altchaChallengeUrl) !== null && _b !== void 0 ? _b : props.config.altchaChallengeUrl;
    const altchaChallengeProvider = (_d = (_c = props.rootParams.globalWidgetConfig) === null || _c === void 0 ? void 0 : _c.altchaChallengeProvider) !== null && _d !== void 0 ? _d : props.config.altchaChallengeProvider;
    const setInternalData = React.useCallback(({ challengeProp, error }) => {
        if (challengeProp) {
            internalData.challengeProp = challengeProp;
        }
        else {
            delete internalData.challengeProp;
        }
        if (error != null) {
            internalData.error = error;
        }
        else {
            delete internalData.error;
        }
        update();
        render();
    }, [internalData, update, render]);
    React.useEffect(() => {
        if (altchaChallengeUrl) {
            setInternalData({ challengeProp: { challengeurl: altchaChallengeUrl } });
        }
        else {
            const result = callConfigFunction(altchaChallengeProvider, 'altchaChallengeProvider', '');
            if (result != null) {
                switch (typeof result) {
                    case 'object':
                        if (typeof result.then === 'function') {
                            result
                                .then((value) => {
                                setInternalData({
                                    challengeProp: { challengejson: typeof value === 'string' ? value : JSON.stringify(value) },
                                });
                            })
                                .catch((error) => {
                                setInternalData({ error: getErrorMessage(error) });
                            });
                        }
                        else {
                            setInternalData({ challengeProp: { challengejson: JSON.stringify(result) } });
                        }
                        break;
                    case 'string':
                        setInternalData({ challengeProp: { challengejson: result } });
                        break;
                    default:
                        setInternalData({ error: t('altcha.invalidChallenge') });
                }
            }
            else {
                setInternalData({ error: t('altcha.invalidProviderResult') });
            }
        }
    }, [altchaChallengeUrl, altchaChallengeProvider, setInternalData]);
    const handleVerified = (result) => {
        props.setDataStringOrUndefined(result);
        delete props.internalData.error;
        renderForm(props, { changed: true, autoSaveDebounce: true });
    };
    const ariaDescribedBy = React.useId();
    return (React.createElement(FormControl, { fullWidth: true, variant: 'outlined', "aria-describedby": ariaDescribedBy, required: true, error: !!error },
        value == null ? (React.createElement(AltchaCdn, { loadingFallback: React.createElement(AltchaBox, { icon: React.createElement(CircularProgress, { size: 18 }), label: (_f = (_e = props.config.labels) === null || _e === void 0 ? void 0 : _e.loading) !== null && _f !== void 0 ? _f : t('common.loading') }) },
            React.createElement(AltchaWidget, { onVerified: handleVerified, labels: props.config.labels, ariaDescribedBy: ariaDescribedBy, challengeProp: props.internalData.challengeProp }))) : (React.createElement(AltchaBox, { icon: React.createElement(CheckIcon, { sx: { color: themeColors.accent.green.bold } }), label: (_h = (_g = props.config.labels) === null || _g === void 0 ? void 0 : _g.verified) !== null && _h !== void 0 ? _h : t('validation.verified') })),
        React.createElement(FormHelperErrorDescription, { descriptionId: ariaDescribedBy, error: error, description: props.config.description })));
};
const WidgetFormAltchaEditor = (props) => {
    var _a, _b, _c, _d;
    const isGlobalAltchaChallengeConfig = ((_a = props.rootParams.globalWidgetConfig) === null || _a === void 0 ? void 0 : _a.altchaChallengeUrl) != null ||
        ((_b = props.rootParams.globalWidgetConfig) === null || _b === void 0 ? void 0 : _b.altchaChallengeProvider) != null;
    const { t } = useCommonFormUiTranslation();
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => {
            var _a, _b;
            return showWidgetEditDialog(props, endEditing, { title: 'ALTCHA: ' + ((_b = (_a = props.config.labels) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : t('widget.widgetGoogleReCaptcha.notRobot')) }, buildGeneralTab(props, {
                key: true,
                label: false,
                description: false,
                children: [
                    ...(isGlobalAltchaChallengeConfig
                        ? [
                            {
                                type: 'form.content',
                                text: t('altcha.globalConfigurationUsed'),
                                style: { alert: 'info' },
                            },
                        ]
                        : [
                            {
                                type: 'form.text',
                                key: 'altchaChallengeUrl',
                                label: t('altcha.challengeUrl'),
                                emptyAsNull: true,
                            },
                            {
                                type: 'form.text',
                                key: 'altchaChallengeProvider',
                                label: t('altcha.challengeProvider'),
                                emptyAsNull: true,
                                multiline: true,
                                rows: 6,
                                style: { monospace: true },
                            },
                        ]),
                    {
                        type: 'form.text',
                        key: 'labels.label',
                        label: t('widget.label'),
                        description: t('widget.widgetGoogleReCaptcha.defaultNotRobot'),
                        emptyAsNull: true,
                    },
                    {
                        type: 'form.text',
                        key: 'labels.verified',
                        label: t('validation.verified'),
                        description: t('validation.defaultVerified'),
                        emptyAsNull: true,
                    },
                    {
                        type: 'form.text',
                        key: 'labels.verifying',
                        label: t('validation.verifying'),
                        description: t('validation.defaultVerifying'),
                        emptyAsNull: true,
                    },
                    {
                        type: 'form.text',
                        key: 'labels.waitAlert',
                        label: t('validation.waitAlert'),
                        description: t('validation.defaultVerifyingWait'),
                        emptyAsNull: true,
                    },
                    {
                        type: 'form.text',
                        key: 'labels.loading',
                        label: t('common.loading') + '...',
                        description: t('validation.defaultLoading'),
                        emptyAsNull: true,
                    },
                    {
                        type: 'form.text',
                        key: 'description',
                        label: t('common.description'),
                        emptyAsNull: true,
                    },
                ],
            }), buildHiddenTab(props, { t }), buildStyleTab(props), buildEventTab(props, { eventOnChange: true, autoSave: true }));
        } }),
        React.createElement(AltchaBox, { icon: React.createElement(CheckIcon, { sx: { color: themeColors.accent.green.bold } }), label: (_d = (_c = props.config.labels) === null || _c === void 0 ? void 0 : _c.label) !== null && _d !== void 0 ? _d : t('widget.widgetGoogleReCaptcha.notRobot') })));
};
export const widgetFormAltcha = {
    type,
    validate: (props) => {
        if (isSkipValidate(props)) {
            return;
        }
        if (props.internalData.error != null) {
            return props.internalData.error;
        }
        const value = props.getDataStringOrUndefined();
        if (!value) {
            return 'Required';
        }
        return validateCustom(props, value);
    },
    update: handleWidgetUpdate,
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        var _a, _b, _c, _d;
        const data = props.getDataStringOrUndefined();
        if (data) {
            return getLabelAndValuePairs(joinLabels(labelPrefix, (_b = (_a = props.config.labels) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : "I'm not a robot"), (_d = (_c = props.config.labels) === null || _c === void 0 ? void 0 : _c.verified) !== null && _d !== void 0 ? _d : 'Verified');
        }
        return [];
    },
    component: WidgetFormAltcha,
    icon: React.createElement(SmartToyIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormAltchaEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, key: 'captcha' }),
};
